import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

interface Notification {
  id: number;
  usuario_id: number;
  tipo: string;
  mensaje: string;
  url: string | null;
  leido: boolean;
  created_at: string;
  partners_source?: boolean;
  source_display?: string;
}

interface UseRealTimeNotificationsReturn {
  isConnected: boolean;
  connectionError: string | null;
  lastNotification: Notification | null;
  connectionStats: {
    reconnectAttempts: number;
    heartbeatCount: number;
    connectedSince: Date | null;
  };
  reconnect: () => void;
}

export const useRealTimeNotifications = (
  onNotificationReceived?: (notification: Notification) => void,
  enableSound: boolean = true
): UseRealTimeNotificationsReturn => {
  const { token, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [lastNotification, setLastNotification] = useState<Notification | null>(null);
  const [connectionStats, setConnectionStats] = useState({
    reconnectAttempts: 0,
    heartbeatCount: 0,
    connectedSince: null as Date | null
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReconnectingRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Configurar audio de notificación
  useEffect(() => {
    if (enableSound) {
      audioRef.current = new Audio('/sounds/notification.mp3');
      audioRef.current.volume = 0.5;
      audioRef.current.preload = 'auto';
      
      // Manejar errores de audio
      audioRef.current.onerror = () => {
        console.warn('Error loading notification sound');
      };
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current = null;
      }
    };
  }, [enableSound]);

  // Función para reproducir sonido
  const playNotificationSound = async () => {
    if (!enableSound || !audioRef.current) return;
    
    try {
      // Verificar que el usuario ha interactuado con la página (requerido por browsers)
      await audioRef.current.play();
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  };

  // Función para conectar SSE con debouncing
  const connect = async () => {
    if (!token || !user || isReconnectingRef.current) {
      return;
    }

    setConnectionError(null);
    isReconnectingRef.current = true;

    // Skip token validation for faster connection - SSE endpoint will validate
    try {
      const url = `${import.meta.env.VITE_API_URL}/api/sse/notifications`;
      const urlWithAuth = `${url}?token=${token}`;
      
      const eventSourceWithAuth = new EventSource(urlWithAuth);
      eventSourceRef.current = eventSourceWithAuth;

      // Evento de conexión exitosa
      eventSourceWithAuth.addEventListener('connected', (event) => {
        setIsConnected(true);
        setConnectionError(null);
        setConnectionStats(prev => ({
          ...prev,
          connectedSince: new Date(),
          reconnectAttempts: 0
        }));
        isReconnectingRef.current = false;
      });

      // Evento de nueva notificación
      eventSourceWithAuth.addEventListener('notification', (event) => {
        try {
          const notification: Notification = JSON.parse(event.data);
          setLastNotification(notification);
          playNotificationSound();
          
          if (onNotificationReceived) {
            onNotificationReceived(notification);
          }
        } catch (error) {
          console.error('Error parsing notification:', error);
        }
      });

      // Evento de heartbeat - solo update stats, no log
      eventSourceWithAuth.addEventListener('heartbeat', (event) => {
        try {
          const data = JSON.parse(event.data);
          setConnectionStats(prev => ({
            ...prev,
            heartbeatCount: data.count || prev.heartbeatCount + 1
          }));
        } catch (error) {
          // Silent fail for heartbeat parsing
        }
      });

      // Evento de shutdown del servidor
      eventSourceWithAuth.addEventListener('shutdown', (event) => {
        setConnectionError('El servidor se está reiniciando');
        setIsConnected(false);
      });

      // Manejo de errores de conexión
      eventSourceWithAuth.onerror = () => {
        setIsConnected(false);
        setConnectionError('Error de conexión con notificaciones');
        
        if (eventSourceWithAuth.readyState === EventSource.CLOSED) {
          scheduleReconnect();
        }
      };

      // No polling - rely on SSE events

    } catch (error) {
      console.error('Error creating SSE connection:', error);
      setConnectionError('Error al inicializar conexión');
      isReconnectingRef.current = false;
      scheduleReconnect();
    }
  };

  // Función para reconectar con backoff exponencial
  const scheduleReconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    const attempt = connectionStats.reconnectAttempts + 1;
    const delay = Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30 segundos
    
    setConnectionStats(prev => ({
      ...prev,
      reconnectAttempts: attempt
    }));

    reconnectTimeoutRef.current = setTimeout(() => {
      if (!isConnected && token && user) {
        disconnect();
        connect();
      }
    }, delay);
  };

  // Función para desconectar
  const disconnect = () => {
    isReconnectingRef.current = false;
    
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setIsConnected(false);
  };

  // Función pública para reconectar manualmente
  const reconnect = () => {
    disconnect();
    setConnectionStats(prev => ({
      ...prev,
      reconnectAttempts: 0,
      connectedSince: null
    }));
    connect();
  };

  // Efecto principal: conectar/desconectar según auth state con debouncing
  useEffect(() => {
    let connectTimeout: NodeJS.Timeout | null = null;

    if (token && user) {
      // Debounce connection to avoid rapid connects/disconnects
      connectTimeout = setTimeout(() => {
        connect();
      }, 1000);
    } else {
      disconnect();
    }

    // Cleanup al desmontar
    return () => {
      if (connectTimeout) {
        clearTimeout(connectTimeout);
      }
      disconnect();
    };
  }, [token, user]);

  return {
    isConnected,
    connectionError,
    lastNotification,
    connectionStats,
    reconnect
  };
};